import { Alert, Button, Spinner } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { keys, useMe } from '../api/hooks.js';
import { AuthLayout } from '../components/AppLayout.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { clearInviteToken, pendingInviteToken, rememberInviteToken, takeHashToken } from '../hash-token.js';

export function CheckEmailPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email;
  return (
    <AuthLayout title={t('auth.checkEmail.title')}>
      <Alert tone="success" title={email ? t('auth.checkEmail.body', { email }) : t('auth.checkEmail.bodyGeneric')} data-testid="check-email" />
      <p className="muted">{t('auth.checkEmail.hint')}</p>
      <p className="auth-links">
        <Link to="/login">{t('auth.checkEmail.loginCta')}</Link>
      </p>
    </AuthLayout>
  );
}

/** Runs a token action once on mount (StrictMode-safe). */
function useTokenAction<T>(action: (token: string) => Promise<T>) {
  const started = useRef(false);
  const [token] = useState(() => takeHashToken());
  const mutation = useMutation({ mutationFn: action });
  const { mutate } = mutation;
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    mutate(token);
  }, [token, mutate]);
  return { token, mutation };
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const { token, mutation } = useTokenAction(async (value) => {
    const result = await api('POST', '/api/auth/verify-email', { token: value });
    await queryClient.invalidateQueries({ queryKey: keys.me });
    return result;
  });
  return (
    <AuthLayout title={t('auth.verify.title')}>
      {!token ? <Alert tone="error" title={t('auth.verify.missingToken')} /> : null}
      {mutation.isPending ? <Spinner label={t('auth.verify.working')} /> : null}
      <ErrorAlert error={mutation.error} />
      {mutation.isSuccess ? (
        <>
          <Alert tone="success" title={t('auth.verify.success')} data-testid="verify-success" />
          {me.data ? (
            <Link className="ui-button ui-button--primary" to="/">
              {t('auth.verify.continue')}
            </Link>
          ) : (
            <Link className="ui-button ui-button--primary" to="/login" data-testid="verify-login">
              {t('auth.verify.loginCta')}
            </Link>
          )}
        </>
      ) : null}
    </AuthLayout>
  );
}

export function InvitePage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token] = useState(() => {
    const fromHash = takeHashToken();
    if (fromHash) rememberInviteToken(fromHash);
    return fromHash ?? pendingInviteToken();
  });
  const preview = useMutation({
    mutationFn: (value: string) =>
      api<{ tenantName: string; email: string; role: 'admin' | 'member' }>('POST', '/api/invites/preview', { token: value }),
  });
  const accept = useMutation({
    mutationFn: () => api<{ tenantId: string; status: string }>('POST', '/api/invites/accept', { token }),
    onSuccess: async () => {
      clearInviteToken();
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: keys.me });
    },
  });
  const started = useRef(false);
  const { mutate } = preview;
  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      mutate(token);
    }
  }, [token, mutate]);

  return (
    <AuthLayout title={t('auth.invite.title')}>
      {!token ? <Alert tone="error" title={t('auth.invite.missingToken')} /> : null}
      <ErrorAlert error={preview.error ?? accept.error} />
      {preview.data && !accept.isSuccess ? (
        <>
          <Alert
            tone="info"
            title={t('auth.invite.body', { tenant: preview.data.tenantName, role: t(`roles.tenant.${preview.data.role}`) })}
          >
            <p>{t('auth.invite.forEmail', { email: preview.data.email })}</p>
          </Alert>
          {me.data ? (
            <Button busy={accept.isPending} onClick={() => accept.mutate()} data-testid="invite-accept">
              {t('auth.invite.accept')}
            </Button>
          ) : (
            <>
              <p className="muted">{t('auth.invite.needLogin')}</p>
              <div className="row">
                <Link className="ui-button ui-button--primary" to="/login">
                  {t('auth.login.title')}
                </Link>
                <Link className="ui-button ui-button--secondary" to="/signup">
                  {t('auth.signup.title')}
                </Link>
              </div>
            </>
          )}
        </>
      ) : null}
      {accept.isSuccess ? (
        <>
          <Alert
            tone="success"
            title={accept.data.status === 'already_member' ? t('auth.invite.alreadyMember') : t('auth.invite.accepted')}
            data-testid="invite-accepted"
          />
          <Button onClick={() => void navigate('/')}>{t('auth.verify.continue')}</Button>
        </>
      ) : null}
    </AuthLayout>
  );
}
